import { CategoryChartQueryFn, CategoryChartQueryParam } from "../categoryChart";
import { SupplyDemandKpiProvider, SupplyDemandKpiProviderBase } from "./categoryChartData.types";
import _ from "lodash";
import { getKpisForProvidersWithCategoryChart } from "../../Components/opendata";
import {
    CONFIDENCE_KEYWORD_IN_SUPPLY_DEMAND_KPI_TITLE,
    // fetchProviderShapesByIds,
    SEPARATOR,
    WaterProvider,
    WATER_SECURITY_SUPPLY_DEMAND_MONTHS_OF_AVAILABLE_SUPPLY_VALUE_TO_LABEL,
    WATER_SUPPLY_DEMAND_MONTHS_OF_AVAILABLE_SUPPLY
} from "../../Components/misc";
import { KpiReport, LongText } from "../../Components/processors";
import { formatNumber } from "utils";

const def = { data: [], initialYear: null, latestYear: null };

const schemeCommentValue = (commentKpiCode: string, report: KpiReport): string => {
    if (report?.value == null) return null;

    if (commentKpiCode !== WATER_SUPPLY_DEMAND_MONTHS_OF_AVAILABLE_SUPPLY) {
        return formatNumber(report?.value);
    }

    return (
        WATER_SECURITY_SUPPLY_DEMAND_MONTHS_OF_AVAILABLE_SUPPLY_VALUE_TO_LABEL?.[report?.value] ??
        formatNumber(report?.value)
    );
};
export const fetchSupplyDemandKpiProviders = async (
    providers: WaterProvider[],
    kpis?: string[][]
): Promise<{ data: SupplyDemandKpiProviderBase[]; latestYear: number; initialYear: number }> => {
    providers = providers.filter(d => d != null && d.tid != null);

    if (!providers || providers?.length === 0) return def;

    if (!kpis || kpis?.length === 0) return def;

    const kpiReportResults = await getKpisForProvidersWithCategoryChart(kpis, providers, false);
    // const providerShapes = await fetchProviderShapesByIds(providers.map(d => d.tid));
    // const termIdToShapes = _.keyBy(providerShapes, d => d?.tid);

    const providerIdSchemeTypeKpiCodeToKpiResult = _.groupBy(kpiReportResults, d =>
        _.join([d?.provider?.tid, d?.schemeType, d?.kpiCode], SEPARATOR)
    );

    const providerIdSchemeTypeKpiCodeYearSchemeToKpiResult = _.groupBy(kpiReportResults, d =>
        _.join([d?.provider?.tid, d?.schemeType, d?.kpiCode, d?.year, d?.scheme], SEPARATOR)
    );

    const years = _.orderBy(_.uniq(kpiReportResults?.map(d => d.year))?.filter(d => d != null) ?? [], d => d, "asc");
    const initialYear = years?.[0];

    const latestYear = years?.[years?.length - 1];

    if (latestYear == null || initialYear == null) return null;

    const confidenceKpis = kpis.filter(kpi => {
        const longLabel = kpi[2];
        return longLabel?.trim()?.toLowerCase()?.indexOf(CONFIDENCE_KEYWORD_IN_SUPPLY_DEMAND_KPI_TITLE) === 0;
    });

    const commentKpis = kpis.filter(kpi => {
        const longLabel = kpi[2];
        return longLabel?.trim()?.toLowerCase()?.indexOf(CONFIDENCE_KEYWORD_IN_SUPPLY_DEMAND_KPI_TITLE) !== 0;
    });

    const providerSchemeComments = providers?.map(provider => {
        const schemeComments = commentKpis.map(commentKpi => {
            const commentKpiCode = commentKpi[0];
            const commentKpiSchemeType = commentKpi[1];
            const commentLabel = commentKpi[2];
            const commentDataKey = _.join([provider?.tid, commentKpiSchemeType, commentKpiCode], SEPARATOR);
            const comments = (providerIdSchemeTypeKpiCodeToKpiResult?.[commentDataKey] ?? []).filter(
                d => d.year === latestYear
            );

            const schemeToComments = _.groupBy(comments, d => d.scheme);

            return {
                commentKpiCode,
                commentKpiSchemeType,
                commentLabel,
                values: providerIdSchemeTypeKpiCodeToKpiResult?.[commentDataKey],
                schemeToComments
            };
        });

        return {
            provider,
            schemeComments
        };
    });

    const providerTIdToSchemeComments = _.keyBy(providerSchemeComments, d => d?.provider?.tid);

    const ret: SupplyDemandKpiProviderBase[] =
        providers?.map(provider => {
            const each: SupplyDemandKpiProviderBase = {
                // providerShape: termIdToShapes?.[provider?.tid]?.geometry,
                // schemeShape: termIdToShapes?.[provider?.tid]?.schemeGeometry,
                waterProvider: provider,
                year: latestYear,
                data: _.flattenDeep(
                    confidenceKpis?.map((kpi, index) => {
                        const kpiCode = kpi[0];
                        const schemeType = kpi[1];
                        const longLabel = kpi[2];
                        const shortLabel = kpi[3];

                        const key = _.join([provider?.tid, schemeType, kpiCode], SEPARATOR);
                        const schemeKpiResults = providerIdSchemeTypeKpiCodeToKpiResult?.[key];
                        const otherKpis = kpis.filter((___, _index) => _index !== index);

                        return (
                            schemeKpiResults
                                ?.filter(d => d.year === latestYear && d.valueStatus != null)
                                ?.map(d => {
                                    const otherKpiComments = _.flattenDeep(
                                        otherKpis.map(otherKpi => {
                                            const otherKpiCode = otherKpi[0];
                                            const otherKpiSchemeType = otherKpi[1];
                                            const longLabel = otherKpi[2];

                                            const otherKpiKey = _.join(
                                                [provider?.tid, otherKpiSchemeType, otherKpiCode, latestYear, d.scheme],
                                                SEPARATOR
                                            );
                                            const values =
                                                providerIdSchemeTypeKpiCodeYearSchemeToKpiResult[otherKpiKey] ?? [];

                                            return values
                                                ?.filter(d => d.valueStatus != null)
                                                ?.map(value => {
                                                    const text = `${longLabel}: ${value?.valueStatus
                                                        ?.toString()
                                                        ?.toUpperCase()}`;
                                                    return {
                                                        processed: text,
                                                        value: text
                                                    } as LongText;
                                                });
                                        })
                                    );

                                    const schemeComments = _.flattenDeep(
                                        providerTIdToSchemeComments?.[provider?.tid]?.schemeComments?.map(
                                            schemeComment => {
                                                const commentLabel = schemeComment?.commentLabel;

                                                return (
                                                    schemeComment?.schemeToComments?.[d?.scheme]
                                                        // ?.filter(value => value?.value != null)
                                                        .map(value => {
                                                            const _value = schemeCommentValue(
                                                                schemeComment.commentKpiCode,
                                                                value
                                                            );
                                                            if (_value == null) return null;

                                                            const text = `${commentLabel}: ${_value}`;

                                                            return {
                                                                processed: text,
                                                                value: text
                                                            } as LongText;
                                                        })
                                                        .filter(d => d != null)
                                                );
                                            }
                                        )
                                    );

                                    return {
                                        entity: d?.scheme,
                                        entityId: d?.schemeId,
                                        kpiCode,
                                        shortLabel,
                                        longLabel,
                                        comments: [...otherKpiComments, ...schemeComments, d?.comment].filter(
                                            d => d != null
                                        ),
                                        value: d.valueStatus
                                    };
                                }) ?? []
                        );
                    }) ?? []
                )
            };

            return each;
        }) ?? [];

    return { data: ret, initialYear, latestYear };
};

export const fetchSupplyDemandKpiProviderChartData: CategoryChartQueryFn = async ({
    categoryChart,
    providers,
    kpis
}: CategoryChartQueryParam): Promise<SupplyDemandKpiProvider> => {
    const provider = providers?.[0];
    if (!provider || !categoryChart || !kpis || kpis?.length === 0) return null;

    const { data: vis, initialYear, latestYear } = await fetchSupplyDemandKpiProviders([provider], kpis);
    if (latestYear == null || initialYear == null || !vis?.[0]) return null;

    const { visHeader, visFooter, displayTitle } = categoryChart;

    const visHeaderProcessed = _.template(visHeader?.value ?? "")({
        startYear: initialYear,
        endYear: latestYear === initialYear ? null : latestYear
    });

    const visFooterProcessed = _.template(visFooter?.value ?? "")({
        startYear: initialYear,
        endYear: latestYear === initialYear ? null : latestYear
    });

    const ret: SupplyDemandKpiProvider = {
        title: displayTitle,
        footer: null,
        vis: {
            ...(vis?.[0] ?? {}),
            header:
                !visHeader || visHeader.processed.trim() === ""
                    ? null
                    : {
                          processed: visHeaderProcessed,
                          value: visHeaderProcessed,
                          format: visHeaderProcessed
                      },
            footer:
                !visFooter || visFooter.processed.trim() === ""
                    ? null
                    : {
                          processed: visFooterProcessed,
                          value: visFooterProcessed,
                          format: visFooterProcessed
                      }
        }
    };

    return ret;
};
